import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, Button, Badge } from '@/components/ui';
import { GripVertical, Pencil, Trash2, MapPin, Car, DollarSign, Calendar, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AutoTransferRule, AutoTransferConditions } from '../../types/auto-transfer.types';

interface SortableRuleCardProps {
  rule: AutoTransferRule;
  onEdit: () => void;
  onDelete: () => void;
}

function buildConditionSummary(conditions: AutoTransferConditions, t: (key: string) => string): {
  geographic: string[];
  vehicle: string[];
  price: string[];
  schedule: string[];
  sources: string[];
} {
  const summary = {
    geographic: [] as string[],
    vehicle: [] as string[],
    price: [] as string[],
    schedule: [] as string[],
    sources: [] as string[],
  };

  // Geographic
  if (conditions.regions && conditions.regions.length > 0) {
    summary.geographic.push(`${conditions.regions.length} ${t('leads.autoTransfer.regions').toLowerCase()}`);
  }
  if (conditions.cities && conditions.cities.length > 0) {
    summary.geographic.push(`${conditions.cities.length} ${t('leads.autoTransfer.cities').toLowerCase()}`);
  }
  if (conditions.neighborhoods && conditions.neighborhoods.length > 0) {
    summary.geographic.push(`${conditions.neighborhoods.length} bairros`);
  }

  // Vehicle
  if (conditions.brandIds && conditions.brandIds.length > 0) {
    summary.vehicle.push(`${conditions.brandIds.length} marcas`);
  }
  if (conditions.modelIds && conditions.modelIds.length > 0) {
    summary.vehicle.push(`${conditions.modelIds.length} modelos`);
  }
  if (conditions.categories && conditions.categories.length > 0) {
    summary.vehicle.push(conditions.categories.join(', '));
  }
  if (conditions.vehicleTypes && conditions.vehicleTypes.length > 0) {
    summary.vehicle.push(conditions.vehicleTypes.join(', '));
  }
  if (conditions.conditions && conditions.conditions.length > 0) {
    summary.vehicle.push(conditions.conditions.map(c => t(`leads.autoTransfer.${c}`)).join(', '));
  }
  if (conditions.fuelTypes && conditions.fuelTypes.length > 0) {
    summary.vehicle.push(conditions.fuelTypes.map(f => t(`leads.autoTransfer.${f}`)).join(', '));
  }
  if (conditions.transmissions && conditions.transmissions.length > 0) {
    summary.vehicle.push(conditions.transmissions.map(tr => t(`leads.autoTransfer.${tr}`)).join(', '));
  }

  // Price and ranges
  if (conditions.minYear || conditions.maxYear) {
    const yearRange = conditions.minYear && conditions.maxYear
      ? `${conditions.minYear}-${conditions.maxYear}`
      : conditions.minYear
      ? `>${conditions.minYear}`
      : `<${conditions.maxYear}`;
    summary.price.push(`Ano: ${yearRange}`);
  }
  if (conditions.minPrice || conditions.maxPrice) {
    const priceRange = conditions.minPrice && conditions.maxPrice
      ? `R$${(conditions.minPrice / 1000).toFixed(0)}k-${(conditions.maxPrice / 1000).toFixed(0)}k`
      : conditions.minPrice
      ? `>R$${(conditions.minPrice / 1000).toFixed(0)}k`
      : conditions.maxPrice
      ? `<R$${(conditions.maxPrice / 1000).toFixed(0)}k`
      : '';
    if (priceRange) summary.price.push(priceRange);
  }
  if (conditions.minMileage || conditions.maxMileage) {
    const mileageRange = conditions.minMileage && conditions.maxMileage
      ? `${(conditions.minMileage / 1000).toFixed(0)}k-${(conditions.maxMileage / 1000).toFixed(0)}k km`
      : conditions.minMileage
      ? `>${(conditions.minMileage / 1000).toFixed(0)}k km`
      : conditions.maxMileage
      ? `<${(conditions.maxMileage / 1000).toFixed(0)}k km`
      : '';
    if (mileageRange) summary.price.push(mileageRange);
  }

  // Schedule
  if (conditions.scheduleDays && conditions.scheduleDays.length > 0) {
    summary.schedule.push(`${conditions.scheduleDays.length} dias`);
  }
  if (conditions.scheduleStart || conditions.scheduleEnd) {
    const timeRange = conditions.scheduleStart && conditions.scheduleEnd
      ? `${conditions.scheduleStart}-${conditions.scheduleEnd}`
      : conditions.scheduleStart
      ? `>${conditions.scheduleStart}`
      : `<${conditions.scheduleEnd}`;
    summary.schedule.push(timeRange);
  }

  // Sources
  if (conditions.sources && conditions.sources.length > 0) {
    summary.sources.push(`${conditions.sources.length} fontes`);
  }
  if (conditions.leadSourceKeys && conditions.leadSourceKeys.length > 0) {
    summary.sources.push(`${conditions.leadSourceKeys.length} chaves`);
  }

  return summary;
}

export function SortableRuleCard({ rule, onEdit, onDelete }: SortableRuleCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const conditionSummary = buildConditionSummary(rule.conditions || {}, t);
  const hasConditions =
    conditionSummary.geographic.length > 0 ||
    conditionSummary.vehicle.length > 0 ||
    conditionSummary.price.length > 0 ||
    conditionSummary.schedule.length > 0 ||
    conditionSummary.sources.length > 0;

  const userNames = rule.assignedUsers?.map(u => u.user.name).join(', ') || '-';

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className={`group relative border-border bg-card transition-shadow ${
          isDragging ? 'shadow-lg ring-2 ring-primary' : 'hover:shadow-md'
        }`}
      >
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <button
              type="button"
              className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing pt-1"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-5 w-5" />
            </button>

            <div className="flex-1 space-y-2">
              {/* Title and status */}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{rule.name}</span>
                {!rule.isActive && (
                  <Badge variant="gray" className="text-xs">
                    {t('leads.autoTransfer.inactive')}
                  </Badge>
                )}
              </div>

              {/* Assigned users */}
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Vendedores:</span> {userNames}
              </div>

              {/* Condition summary */}
              {hasConditions ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  {conditionSummary.geographic.length > 0 && (
                    <Badge variant="info" className="text-xs gap-1">
                      <MapPin className="h-3 w-3" />
                      {conditionSummary.geographic.join(' • ')}
                    </Badge>
                  )}
                  {conditionSummary.vehicle.length > 0 && (
                    <Badge variant="info" className="text-xs gap-1">
                      <Car className="h-3 w-3" />
                      {conditionSummary.vehicle.join(' • ')}
                    </Badge>
                  )}
                  {conditionSummary.price.length > 0 && (
                    <Badge variant="info" className="text-xs gap-1">
                      <DollarSign className="h-3 w-3" />
                      {conditionSummary.price.join(' • ')}
                    </Badge>
                  )}
                  {conditionSummary.schedule.length > 0 && (
                    <Badge variant="info" className="text-xs gap-1">
                      <Calendar className="h-3 w-3" />
                      {conditionSummary.schedule.join(' • ')}
                    </Badge>
                  )}
                  {conditionSummary.sources.length > 0 && (
                    <Badge variant="info" className="text-xs gap-1">
                      <Zap className="h-3 w-3" />
                      {conditionSummary.sources.join(' • ')}
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  Sem condições específicas (aceita todos os leads)
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
